if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }
    
      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          
          if (!variant) {
            this.setUnavailable();
            return;
          }
          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('price1');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
          
          
        };
      }
     
      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          console.log(input.value + 'input value')
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        
          const visibleBlocks = document.querySelectorAll(
            '.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)'
          );
          let labelCount = 0;
          visibleBlocks.forEach(block => {
            const app_variantId = block.getAttribute('data-variant-id'); 
            if(app_variantId == variantId ){
              const labels = block.querySelectorAll('.shopify_subscriptions_fieldset .shopify_subscriptions_app_block_label');
             const input = labels.length > 0 ? labels[0].querySelector('input') : null;
             const dataRadioType = input ? input.getAttribute('data-radio-type') : null;
              if(dataRadioType == 'one_time_purchase'){
                $('.price_pdp').removeClass('subscribe');
                input.checked = true;
                      const name = input.name;
                      if (name) {
                        // Find all radios with the same name but exclude the current one
                        const otherRadios = document.querySelectorAll(`input[type="radio"][name="${name}"]:not(:checked)`);
                        otherRadios.forEach(radio => {
                          radio.checked = false;
                        });
                      }
              }
              else{
                $('.price_pdp').addClass('subscribe');
              }
            console.log(dataRadioType);

              labelCount += labels.length;
               if (labelCount < 2) {
                    //block.style.display = 'none';
                }
            }
            const inputs = block.querySelectorAll('.shopify_subscriptions_fieldset .shopify_subscriptions_app_block_label input');
            inputs.forEach(input => {
              input.addEventListener('change', function () {
                const type = this.getAttribute('data-radio-type');
                if (type === 'selling_plan') {
                  console.log('selling_plan ');
                 $('.free_ship').text('+ Free Shipping');
                } else {
                    console.log('NONselling_plan ');
                  $('.free_ship').text('+ USD $8.50 Shipping');
                }
              });
            });
          });
          console.log('Visible label count:', labelCount);
         
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return ;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
        const currentScroll = window.scrollY;
        setTimeout(() => {
            window.scrollTo({ top: currentScroll });
          }, 10);
      }
      
      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'price1', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }
      
      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      // setQuantityBoundries() {
        
      //   const data = {
      //     cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
      //     min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
      //     max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
      //     step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
      //   };

      //   let min = data.min;
      //   const max = data.max === null ? data.max : data.max - data.cartQuantity;
      //   if (max !== null) min = Math.min(min, max);
      //   if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

      //   this.quantityInput.min = min;

      //   if (max) {
      //     this.quantityInput.max = max;
      //   } else {
      //     this.quantityInput.removeAttribute('max');
      //   }
      //   this.quantityInput.value = min;

      //   publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      // }
      setQuantityBoundries() {
        const customerTags = (this.closest('[data-customer-tags]')?.dataset.customerTags || '').split(',');
        const isPractitioner = customerTags.includes('Practitioner');
      
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseFloat(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseFloat(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseFloat(this.quantityInput.step) : 1,
        };
      
        let min = isPractitioner ? 8 : data.min;
        const step = isPractitioner ? 8 : data.step;
      
        let max = data.max === null ? null : data.max - data.cartQuantity;
      
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, step);
      
        this.quantityInput.min = min;
        this.quantityInput.step = step;
      
        if (max !== null) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
      
        // Ensure value respects Practitioner step rules
        this.quantityInput.value = min;
      
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      // updateQuantityRules(sectionId, html) {
      //   if (!this.quantityInput) return;
      //   this.setQuantityBoundries();

      //   const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
      //   const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
      //   for (let selector of selectors) {
      //     const current = this.quantityForm.querySelector(selector);
      //     const updated = quantityFormUpdated.querySelector(selector);
      //     if (!current || !updated) continue;
      //     if (selector === '.quantity__input') {
      //       const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
      //       for (let attribute of attributes) {
      //         const valueUpdated = updated.getAttribute(attribute);
      //         if (valueUpdated !== null) {
      //           current.setAttribute(attribute, valueUpdated);
      //         } else {
      //           current.removeAttribute(attribute);
      //         }
      //       }
      //     } else {
      //       current.innerHTML = updated.innerHTML;
      //     }
      //   }
      // }
      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
      
        const customerTags = (this.closest('[data-customer-tags]')?.dataset.customerTags || '').split(',');
        const isPractitioner = customerTags.includes('Practitioner');
      
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
      
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
      
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
      
            // Override for Practitioner
            if (isPractitioner) {
              current.setAttribute('data-min', '8');
              current.setAttribute('step', '8');
              current.setAttribute('min', '8');
              current.setAttribute('value', '8');
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      
        this.setQuantityBoundries(); // Apply logic after updating attributes
      }


      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

/**************SUBSCRIPTION APP******************/
document.addEventListener("DOMContentLoaded", function () {
  setTimeout(function () {
    const visibleBlocks = document.querySelectorAll('.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)');

    visibleBlocks.forEach(block => {
      const inputs = block.querySelectorAll('.shopify_subscriptions_fieldset .shopify_subscriptions_app_block_label input');

      inputs.forEach(input => {
        input.addEventListener('change', function () {
          const type = this.getAttribute('data-radio-type');
          if (type === 'selling_plan') {
            console.log('selling_plan ');
           $('.free_ship').text('+ Free Shipping');
          } else {
              console.log('NONselling_plan ');
            $('.free_ship').text('+ USD $8.50 Shipping');
          }
        });
      });
    });
  }, 1000);
});


//benefit slider 
new Swiper('.benefits-slider', {
  slidesPerView: 'auto',
  spaceBetween: 20,
  freeMode: true,
  pagination: {
    el: '.benefits-pagination',
    type: 'progressbar',
  },
});
function copyToClipboard(button, text) {
    navigator.clipboard.writeText(text).then(function () {
      // Optional: show a "Copied!" tooltip/message
      const original = button.innerHTML;
      button.innerHTML = '✔️ Copied';
      setTimeout(() => {
        button.innerHTML = original;
      }, 2000);
    }).catch(function (err) {
      console.error('Failed to copy: ', err);
    });
  }
